/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Close, Save } from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import PageContainer from "@jumbo/components/PageContainer";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import { istToday, istDateInput } from "app/helpers/istDate";
import { OFFICE_INVOICES, OFFICE_ORDER_DISPATCHED } from "app/routes/constant";
import {
  createInvoice,
  getByIdInvoice,
  getOrderNo,
  updateInvoice,
} from "app/redux/slice/invoiceSlice";
import { getProductList } from "app/redux/slice/productSlice";
import { getVariantList } from "app/redux/slice/variantSlice";
import { getSupplierList } from "app/redux/slice/supplierSlice";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { getByIdPurchaseOrder } from "app/redux/slice/purchaseOrderSlice";
import Loader from "@jumbo/components/Loader";

const useStyles = makeStyles({
  headingContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  mainInfoContainer: {
    display: "flex",
    minHeight: "170px",
    justifyContent: "center",
    flexDirection: "column",
  },
  headingTitleContainer: {
    display: "flex",
    flexGrow: "wrap",
  },
  headingTitle: {
    minWidth: "80px",
  },
  headingContent: {
    fontWeight: "500",
  },
  qrContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  table: {
    minWidth: 650,
    "& .MuiTableCell-root": {
      borderLeft: "1px solid",
      borderBottom: "1px solid",
    },
  },
  particular: {
    minWidth: "200px",
  },
  amount: {
    minWidth: "100px",
    verticalAlign: "top",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "10px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  alignTop: {
    verticalAlign: "top",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
});

const validationSchema = yup.object({
  supplierId: yup.object().required("Please select vendor").nullable(),
  invoiceNumber: yup.string().required("Please enter invoice number."),
  challanNumber: yup
    .number()
    .typeError("Challan number must be a number")
    .nullable(),
  invoiceDate: yup
    .string()
    .required("Please enter date.")
    // The picker's `max` blocks it too, but a typed value would slip past that.
    .test("not-future", "Invoice date cannot be in the future.", (value) =>
      value ? value <= istToday() : true
    ),
  products: yup.array().of(
    yup.object().shape({
      productId: yup.object().required("Please select product").nullable(),
      variantId: yup.object().required("Please select variant").nullable(),
      // quantity: yup.number().required("Please enter quantity").min(1),
      totalWeight: yup.number().required("Please enter total weight").min(1),
      unitPrice: yup.number().required("Please enter unit price").min(0),
    }),
  ),
});

const AddAndUpdateInvoice = () => {
  const { id, poId, invoiceId } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const supplierData = useSelector((state) => state?.supplier);
  const productData = useSelector((state) => state?.product);
  const variantData = useSelector((state) => state?.variant);
  const mobileView = useMediaQuery("(max-width:575px)");
  const [loading, setLoading] = useState(true);
  const [weightKgMap, setWeightKgMap] = useState({});

  // One id per opened form, sent with the create request so the server can tell
  // a resubmission of the same invoice apart from a genuinely new one. Minted
  // once (useRef, not state) so it survives every re-render and every retry of
  // the same form.
  const requestIdRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `inv-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
  );

  let sum = 0;

  const formik = useFormik({
    initialValues: {
      supplierId: null,
      invoiceNumber: "",
      challanNumber: "",
      invoiceDate: istToday(),
      invoiceAmount: "",
      gst: 5,
      gstAmount: "",
      amountWithoutTax: "",
      // Coupon details, carried through read-only from the source Purchase
      // Order when generating an invoice from one - never recalculated here.
      couponCode: "",
      discountType: "",
      discountValue: "",
      discountAmount: 0,
      products: [
        {
          _id: 1,
          productId: null,
          variantId: null,
          quantity: "",
          totalWeight: "",
          unitPrice: 0,
          amount: "",
        },
      ],
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      const productArr = [];

      values.products?.forEach((val) => {
        productArr.push({
          productId: val?.productId?._id,
          variantId: val?.variantId?._id,
          quantity: val.quantity,
          totalWeight: Number(val.totalWeight.toFixed(2)),
          unitPrice: val.unitPrice,
          amount: val.amount,
        });
      });
      setSubmitting(true);
      const newInvoice = {
        ...(poId && { poId }),
        // Identifies THIS filled-in form. If it reaches the server twice the
        // second one is recognised and ignored instead of saving the same
        // invoice again. Not sent on edit - that targets an existing record.
        ...(!id && { requestId: requestIdRef.current }),
        supplierId: values?.supplierId?._id,
        invoiceNumber: values?.invoiceNumber,
        challanNumber: values?.challanNumber,
        invoiceDate: values?.invoiceDate,
        invoiceAmount: values?.invoiceAmount || Number(sum.toFixed(2)),
        gst: values.gst,
        gstAmount: values.gstAmount,
        amountWithoutTax: values.amountWithoutTax,
        couponCode: values.couponCode || null,
        discountType: values.discountType || null,
        discountValue: values.discountValue || null,
        discountAmount: values.discountAmount || 0,
        products: productArr,
        subTotal: Number(sum.toFixed(2)),
      };
      const response = await dispatch(
        id
          ? updateInvoice({ id, data: newInvoice })
          : createInvoice(newInvoice),
      ).unwrap();
      if (response.status === 200) {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "success",
        });
        navigate(OFFICE_INVOICES);
        resetForm();
      } else {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "error",
        });
      }
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, errors, isSubmitting } = formik;
  const productsLength = values?.products?.length;

  const handleChange = (index, field, value) => {
    let updatedFields = [...values.products];

    if (field === "unitPrice") {
      const newPrice = parseFloat(value) || 0;
      const currentProduct = updatedFields[index];
      const currentProductId = currentProduct.productId?._id;

      // Update current row
      updatedFields[index] = {
        ...currentProduct,
        unitPrice: newPrice,
        amount: Number(
          ((parseFloat(currentProduct.totalWeight) || 0) * newPrice).toFixed(2),
        ),
      };

      // Update duplicates
      if (currentProductId) {
        updatedFields = updatedFields.map((item, idx) => {
          if (idx !== index && item.productId?._id === currentProductId) {
            return {
              ...item,
              unitPrice: newPrice,
              amount: Number(
                ((parseFloat(item.totalWeight) || 0) * newPrice).toFixed(2),
              ),
            };
          }
          return item;
        });
      }
    } else if (field === "totalWeight") {
      const weight = parseFloat(value) || 0;
      const currentProduct = updatedFields[index];
      const price = parseFloat(currentProduct.unitPrice) || 0;
      updatedFields[index] = {
        ...currentProduct,
        totalWeight: weight,
        amount: Number((weight * price).toFixed(2)),
      };
    } else {
      updatedFields[index] = { ...updatedFields[index], [field]: value };

      // If productId changed, inherit price from existing same product or default to 0
      if (field === "productId" && value) {
        const existingProduct = updatedFields.find(
          (item, idx) => idx !== index && item.productId?._id === value._id,
        );
        if (existingProduct) {
          updatedFields[index].unitPrice = existingProduct.unitPrice;
          const weight = parseFloat(updatedFields[index].totalWeight) || 0;
          updatedFields[index].amount = Number(
            (weight * (parseFloat(existingProduct.unitPrice) || 0)).toFixed(2),
          );
        } else {
          updatedFields[index].unitPrice = 0;
          updatedFields[index].amount = 0;
        }
      }
    }

    let totalSum = 0;
    updatedFields.forEach((item) => {
      totalSum += parseFloat(item.amount) || 0;
    });

    // Coupon discount (0 for every non-PO / no-coupon invoice, so this is a
    // no-op there) is subtracted before GST, same as the Supplier's own
    // checkout calculation (modules/app/helper/order-calculation.js).
    const discountAmount = parseFloat(values?.discountAmount) || 0;
    const taxableAmount = totalSum - discountAmount;
    const gstRate = parseFloat(values?.gst) || 0;
    const gstAmount = (taxableAmount * gstRate) / 100;
    const amountWithoutTax = taxableAmount - gstAmount;
    const invoiceAmount = taxableAmount + gstAmount;

    setFieldValue("gstAmount", gstAmount);
    setFieldValue("amountWithoutTax", amountWithoutTax);
    setFieldValue("invoiceAmount", invoiceAmount);
    setFieldValue("products", updatedFields);
  };

  // Helper function to get weight breakdown tooltip
  const getWeightTooltip = (productId, variantId) => {
    if (!weightKgMap || !productId || !variantId) return null;

    const productWeights = weightKgMap[productId];
    if (!productWeights || !productWeights[variantId]) return null;

    const weights = productWeights[variantId];
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);

    return (
      <div style={{ padding: "8px" }}>
        <div
          style={{
            fontWeight: "bold",
            marginBottom: "8px",
            borderBottom: "1px solid rgba(255,255,255,0.3)",
            paddingBottom: "6px",
            fontSize: "13px",
          }}
        >
          Weight Breakdown
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {weights.map((w, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "12px",
                gap: "16px",
              }}
            >
              <span style={{ opacity: 0.9 }}>Roll {i + 1}:</span>
              <span style={{ fontWeight: "500" }}>{w}</span>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: "8px",
            paddingTop: "6px",
            borderTop: "1px solid rgba(255,255,255,0.3)",
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "bold",
            fontSize: "13px",
          }}
        >
          <span>Total:</span>
          <span>{totalWeight.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (id || invoiceId) {
      setLoading(true);
      dispatch(getByIdInvoice(id || invoiceId))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const invoiceDetail = response?.data?.data?.invoiceDetail;
            const weightMapData = response?.data?.data?.weightKgMap || {};

            // Store weightKgMap
            setWeightKgMap(weightMapData);

            let productArr = [];
            setFieldValue("supplierId", invoiceDetail?.supplierId);
            setFieldValue("invoiceNumber", invoiceDetail?.invoiceNumber);
            setFieldValue("challanNumber", invoiceDetail?.challanNumber);
            setFieldValue(
              "invoiceDate",
              istDateInput(invoiceDetail?.invoiceDate),
            );
            setFieldValue("invoiceAmount", invoiceDetail?.invoiceAmount);
            setFieldValue("gst", invoiceDetail?.gst);
            setFieldValue("gstAmount", invoiceDetail?.gstAmount);
            setFieldValue("amountWithoutTax", invoiceDetail?.amountWithoutTax);
            setFieldValue("couponCode", invoiceDetail?.couponCode || "");
            setFieldValue("discountType", invoiceDetail?.discountType || "");
            setFieldValue("discountValue", invoiceDetail?.discountValue ?? "");
            setFieldValue("discountAmount", invoiceDetail?.discountAmount || 0);

            invoiceDetail?.products?.forEach((val) => {
              productArr.push({
                productId: val?.productId,
                variantId: val?.variantId,
                quantity: val?.quantity,
                totalWeight: Number(val.totalWeight.toFixed(2)),
                unitPrice: val?.unitPrice,
                amount: Number(val?.amount.toFixed(2)),
              });
            });
            setFieldValue("products", productArr);
            setLoading(false);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
            setLoading(false);
          }
        });
    }
    if (poId) {
      setLoading(true);
      dispatch(getByIdPurchaseOrder(poId))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const purchaseOrderDetail =
              response?.data?.data?.purchaseOrderDetail;
            let productArr = [];

            // Build weightKgMap from items array
            const poWeightMap = {};
            purchaseOrderDetail?.products?.forEach((product) => {
              const productId = product?.productId?._id;
              const variantId = product?.variantId?._id;

              if (productId && variantId && product?.items?.length > 0) {
                if (!poWeightMap[productId]) {
                  poWeightMap[productId] = {};
                }
                poWeightMap[productId][variantId] = product.items.map(
                  (item) => item.weightKg,
                );
              }
            });

            // Store weightKgMap
            setWeightKgMap(poWeightMap);

            setFieldValue("supplierId", purchaseOrderDetail?.supplierId);
            setFieldValue("challanNumber", purchaseOrderDetail?.challanNumber);
            // A purchase order has no invoiceDate of its own - the invoice is
            // being raised right now, so it is dated today. This used to read
            // `moment(purchaseOrderDetail?.invoiceDate)`, which only produced
            // today's date because moment(undefined) silently means "now";
            // spelling it out keeps the same result without relying on that.
            setFieldValue(
              "invoiceDate",
              istDateInput(purchaseOrderDetail?.invoiceDate) || istToday(),
            );
            setFieldValue("invoiceAmount", purchaseOrderDetail?.invoiceAmount);
            setFieldValue("gst", 5);
            setFieldValue("couponCode", purchaseOrderDetail?.couponCode || "");
            setFieldValue("discountType", purchaseOrderDetail?.discountType || "");
            setFieldValue(
              "discountValue",
              purchaseOrderDetail?.discountValue ?? "",
            );
            setFieldValue(
              "discountAmount",
              purchaseOrderDetail?.discountAmount || 0,
            );

            if (purchaseOrderDetail?.supplierId?._id) {
              const value = {
                supplierId: purchaseOrderDetail?.supplierId?._id,
                sort: 1,
                field: "name",
              };

              dispatch(getProductList(value))
                .unwrap()
                .then((response) => {
                  // const resData = response?.data?.data?.productList;
                  let productTotal = 0;
                  purchaseOrderDetail?.products?.forEach((val, index) => {
                    // const productData = resData?.find(
                    //   (item) => item._id === val?.productId?._id,
                    // );

                    productArr.push({
                      productId: val?.productId,
                      variantId: val?.variantId,
                      quantity: val?.quantity,

                      totalWeight: Number(val.totalWeight.toFixed(2)),
                      unitPrice: val?.unitPrice || 0,
                      amount: Number(
                        (+val?.unitPrice * +val?.totalWeight).toFixed(2),
                      ),
                    });
                    productTotal += Number(
                      (+val?.unitPrice * +val?.totalWeight).toFixed(2),
                    );
                  });
                  setFieldValue("products", productArr);

                  setFieldValue("gst", 5);
                  // Same discount-before-GST order as the Supplier's own
                  // checkout calculation - reused, not recalculated.
                  const discountAmount = purchaseOrderDetail?.discountAmount || 0;
                  const taxableAmount = productTotal - discountAmount;
                  const gstRate = 5;
                  const gstAmount = (taxableAmount * gstRate) / 100;
                  const amountWithoutTax = taxableAmount - gstAmount;
                  setFieldValue("gstAmount", gstAmount);
                  setFieldValue("amountWithoutTax", amountWithoutTax);
                  setFieldValue("invoiceAmount", taxableAmount + gstAmount);
                });
            }
            setLoading(false);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
            setLoading(false);
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id, setFieldValue]);

  useEffect(() => {
    if (!id || poId) {
      dispatch(getOrderNo({ type: "invoice" }))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const newPoNumber = response?.data?.data?.orderNo;
            setFieldValue("invoiceNumber", newPoNumber);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }

    dispatch(getVariantList({ field: "name", sort: 1 }));
    dispatch(getSupplierList({ sort: 1, field: "name", type: "Office" }));
  }, []);

  const { handleSubmit } = formik;
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <PageContainer
          heading={
            invoiceId
              ? "Invoice Details"
              : id
              ? "Update Invoice"
              : "Add Invoice"
          }
        >
          <Card>
            <CardContent>
              <FormikProvider value={formik}>
                {/* A save already in flight must not be started again. The Save
                    button now disables itself, but Enter inside any field
                    submits the form directly and bypasses it. */}
                <Form
                  noValidate
                  autoComplete="off"
                  onSubmit={(event) => {
                    if (isSubmitting) {
                      event.preventDefault();
                      return;
                    }
                    handleSubmit(event);
                  }}
                >
                  <Grid container border={1}>
                    <Grid item sm={3} xs={12} borderRight={{ xs: 0, sm: 1 }}>
                      <Grid>
                        <Grid px={1} pt={1} mb={1}>
                          <JumboAutoComplete
                            disabled
                            options={supplierData.list ?? []}
                            label="Vendor*"
                            size="small"
                            getOptionLabel={(option) => option?.name}
                            name={"supplierId"}
                            value={values?.supplierId}
                            onChange={(evt, value) => {
                              setFieldValue("supplierId", value);
                            }}
                            helperText={formik?.errors?.supplierId}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item sm={9} xs={12}>
                      <Grid container>
                        <Grid
                          item
                          sm={4}
                          xs={12}
                          borderRight={{ xs: 0, sm: 1 }}
                          p={1}
                        >
                          <TextField
                            disabled
                            label={"Invoice No."}
                            name="invoiceNumber"
                            fullWidth
                            size="small"
                            value={values?.invoiceNumber}
                            onChange={(evt) =>
                              setFieldValue("invoiceNumber", evt.target.value)
                            }
                            error={errors?.invoiceNumber}
                            helperText={errors?.invoiceNumber}
                          />
                        </Grid>
                        <Grid
                          item
                          sm={4}
                          invoiceAmount
                          xs={12}
                          borderRight={{ xs: 0, sm: 1 }}
                          p={1}
                        >
                          <TextField
                            disabled
                            label={"Date"}
                            name="invoiceDate"
                            fullWidth
                            size="small"
                            type="date"
                            onChange={(evt, value) =>
                              setFieldValue("invoiceDate", value)
                            }
                            InputLabelProps={{ shrink: true }}
                            // Today in India - stops a future date being picked.
                            inputProps={{ max: istToday() }}
                            value={values?.invoiceDate}
                            error={errors?.invoiceDate}
                            helperText={errors?.invoiceDate}
                          />
                        </Grid>
                        <Grid item sm={4} xs={12} p={1}>
                          <TextField
                            label={"Challan No."}
                            name="challanNumber"
                            fullWidth
                            size="small"
                            type="text"
                            value={values?.challanNumber}
                            onChange={(evt) =>
                              setFieldValue("challanNumber", evt.target.value)
                            }
                            error={errors?.challanNumber}
                            helperText={errors?.challanNumber}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    borderRight={1}
                    borderBottom={1}
                    sx={{
                      mt: { xs: 3, md: 0 },
                    }}
                    className="mobile-view-table-1"
                  >
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 650 }}
                        size="small"
                        aria-label="a dense table"
                        className={classes?.table}
                      >
                        <TableHead>
                          <TableRow>
                            {!mobileView ? <TableCell>Sr. No.</TableCell> : ""}
                            <TableCell className={classes?.particular}>
                              Particular
                            </TableCell>
                            <TableCell>Variant</TableCell>
                            <TableCell>Total Quantity</TableCell>
                            <TableCell>Total Weight</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Total</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {productsLength &&
                            values?.products?.map((item, index) => {
                              sum += item?.amount ? +item?.amount : 0;
                              const tooltipText = getWeightTooltip(
                                item?.productId?._id,
                                item?.variantId?._id,
                              );

                              return (
                                <TableRow className="product-invoice-tr">
                                  {!mobileView ? (
                                    <TableCell>{index + 1}</TableCell>
                                  ) : (
                                    ""
                                  )}
                                  <TableCell
                                    sx={{
                                      minWidth: "250px",
                                      maxWidth: "250px",
                                    }}
                                  >
                                    <JumboAutoComplete
                                      disabled
                                      options={productData.list ?? []}
                                      label="Product*"
                                      size="small"
                                      getOptionLabel={(option) => option?.name}
                                      name={`products[${index}].productId`}
                                      value={item?.productId}
                                      onChange={(evt, value) => {
                                        handleChange(index, "productId", value);
                                        handleChange(index, "variantId", null);
                                      }}
                                      helperText={
                                        formik?.errors?.products?.length &&
                                        formik?.errors?.products[index]
                                          ?.productId
                                      }
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      minWidth: "250px",
                                      maxWidth: "250px",
                                    }}
                                  >
                                    <JumboAutoComplete
                                      disabled
                                      options={
                                        item?.productId
                                          ? variantData?.list.filter(
                                              (list) =>
                                                list?.product?._id ===
                                                item?.productId?._id,
                                            ) || []
                                          : []
                                      }
                                      label="Variant*"
                                      size="small"
                                      getOptionLabel={(option) => option?.name}
                                      name={`products[${index}].variantId`}
                                      value={item?.variantId}
                                      onChange={(evt, value) =>
                                        handleChange(index, "variantId", value)
                                      }
                                      helperText={
                                        formik?.errors?.products?.length &&
                                        formik?.errors?.products[index]
                                          ?.variantId
                                      }
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      minWidth: "110px",
                                      maxWidth: "110px",
                                    }}
                                  >
                                    <TextField
                                      disabled
                                      sx={{ marginTop: "12px" }}
                                      placeholder="Qty"
                                      label="Total Quantity"
                                      name={`products[${index}].quantity`}
                                      fullWidth
                                      value={item?.quantity}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      minWidth: "110px",
                                      maxWidth: "110px",
                                    }}
                                  >
                                    <Tooltip
                                      title={tooltipText || ""}
                                      arrow
                                      placement="right"
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: "rgba(50, 50, 50, 0.95)",
                                            color: "white",
                                            boxShadow:
                                              "0 4px 12px rgba(0,0,0,0.15)",
                                            padding: 0,
                                            maxWidth: "300px",
                                            fontSize: "13px",
                                            "& .MuiTooltip-arrow": {
                                              color: "rgba(50, 50, 50, 0.95)",
                                            },
                                          },
                                        },
                                      }}
                                    >
                                      <div style={{ position: "relative" }}>
                                        <TextField
                                          disabled
                                          sx={{
                                            marginTop: "12px",
                                            "& .MuiInputBase-root": {
                                              cursor: tooltipText
                                                ? "help"
                                                : "default",
                                              "&:hover": {
                                                backgroundColor: tooltipText
                                                  ? "rgba(0, 0, 0, 0.02)"
                                                  : "transparent",
                                              },
                                            },
                                          }}
                                          placeholder="Qty"
                                          label="Total Weight"
                                          name={`products[${index}].totalWeight`}
                                          fullWidth
                                          value={item?.totalWeight}
                                          size="small"
                                          onChange={(evt) =>
                                            handleChange(
                                              index,
                                              "totalWeight",
                                              +evt.target.value,
                                            )
                                          }
                                          error={
                                            errors?.products?.length &&
                                            errors?.products[index]?.totalWeight
                                          }
                                          helperText={
                                            errors?.products?.length &&
                                            errors?.products[index]?.totalWeight
                                          }
                                        />
                                        {tooltipText && (
                                          <div
                                            style={{
                                              position: "absolute",
                                              top: "22px",
                                              right: "8px",
                                              width: "18px",
                                              height: "18px",
                                              borderRadius: "50%",
                                              backgroundColor: "#1976d2",
                                              color: "white",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              fontSize: "11px",
                                              fontWeight: "bold",
                                              cursor: "help",
                                              pointerEvents: "none",
                                              boxShadow:
                                                "0 2px 4px rgba(0,0,0,0.2)",
                                            }}
                                          >
                                            i
                                          </div>
                                        )}
                                      </div>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      minWidth: "110px",
                                      maxWidth: "110px",
                                    }}
                                  >
                                    <TextField
                                      sx={{ marginTop: "12px" }}
                                      placeholder="Price"
                                      label="Price"
                                      name={`products[${index}].unitPrice`}
                                      fullWidth
                                      disabled
                                      type="text"
                                      value={item?.unitPrice}
                                      size="small"
                                      onChange={(evt) =>
                                        handleChange(
                                          index,
                                          "unitPrice",
                                          +evt.target.value,
                                        )
                                      }
                                      error={
                                        errors?.products?.length &&
                                        errors?.products[index]?.unitPrice
                                      }
                                      helperText={
                                        errors?.products?.length &&
                                        errors?.products[index]?.unitPrice
                                      }
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      minWidth: "110px",
                                      maxWidth: "110px",
                                    }}
                                  >
                                    <TextField
                                      disabled
                                      sx={{ marginTop: "12px" }}
                                      placeholder="Total"
                                      label="Total"
                                      name={`products[${index}].amount`}
                                      fullWidth
                                      value={item?.amount}
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          <TableRow>
                            <TableCell colSpan={6} align="right">
                              <b>Subtotal</b>
                            </TableCell>
                            <TableCell>
                              <b>{Number(sum.toFixed(2))}</b>
                            </TableCell>
                          </TableRow>
                          {values?.discountAmount > 0 && (
                            <TableRow>
                              <TableCell colSpan={6} align="right">
                                Coupon Discount
                                {values?.couponCode
                                  ? ` (${values.couponCode})`
                                  : ""}
                              </TableCell>
                              <TableCell>
                                -{Number(values.discountAmount).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      sm={12}
                      xs={12}
                      className={classes?.loadingButtonContainer}
                    >
                      {!invoiceId ? (
                        <LoadingButton
                          type="submit"
                          buttonLabel={id ? "Update" : "Save"}
                          sx={{ marginRight: "10px" }}
                          startIcon={<Save />}
                          variant="outlined"
                          fullWidth={mobileView}
                          // Without this the button stayed clickable for the
                          // whole save, so a second click while the first was
                          // still in flight wrote a second invoice - which is
                          // how two rows ended up sharing an invoice number.
                          loading={isSubmitting}
                          disabled={isSubmitting}
                        />
                      ) : (
                        ""
                      )}
                      <LoadingButton
                        color={"secondary"}
                        buttonLabel={"Close"}
                        variant="outlined"
                        startIcon={<Close />}
                        onClick={() =>
                          navigate(
                            invoiceId
                              ? OFFICE_ORDER_DISPATCHED
                              : OFFICE_INVOICES,
                          )
                        }
                        fullWidth={mobileView}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </FormikProvider>
            </CardContent>
          </Card>
        </PageContainer>
      )}
    </>
  );
};

export default AddAndUpdateInvoice;
